
import TaMoAkiLogo from '@/components/branding/TaMoAkiLogo';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { Bell, FileText, HelpCircle, LogOut, MapPin, Shield, User, X, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface DrawerMenuProps {
  id?: string;
  isOpen: boolean;
  onClose: () => void;
}

const DrawerMenu = ({ isOpen, onClose }: DrawerMenuProps) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const menuItems = [
    { icon: MapPin, label: 'Prestadores na sua zona', path: '/nearby-search' },
    { icon: Bell, label: 'Notificações', path: '/notifications' },
    { icon: Heart, label: 'Favoritos', path: '/favorites' },
    { icon: HelpCircle, label: 'Centro de Ajuda', path: '/ajuda' },
    { icon: Shield, label: 'Política de Privacidade', path: '/privacidade' },
    { icon: FileText, label: 'Termos de Uso', path: '/termos' },
  ];

  const handleLogout = async () => {
    // Navegar ANTES de limpar a sessão — ver Sidebar.tsx/Settings.tsx para
    // o porquê (corrida com o redirect para /login do ProtectedRoute).
    navigate('/');
    onClose();
    await signOut();
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300">
        {/* Logotipo e nome */}
        <div className="p-4 border-b border-slate-200">
          <TaMoAkiLogo size="md" showText={true} />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Menu</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* User Profile Section */}
        <div className="p-6 border-b">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">
                {user?.user_metadata?.full_name || 'Utilizador'}
              </h3>
              <p className="text-sm text-gray-600">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="flex flex-col p-4">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={() => handleMenuClick(item.path)}
                className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Logout Button */}
        <div className="absolute bottom-6 left-4 right-4">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full flex items-center justify-center space-x-2 text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" />
            <span>Sair da Conta</span>
          </Button>
        </div>
      </div>
    </>
  );
};

export default DrawerMenu;
